import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Plus, Upload, ImagePlus } from "lucide-react";

const empty = { name: "", description: "", price: "", is_veg: true, category_id: "", image_url: "", video_url: "", tags: "", is_available: true, tax_rate: 5 };

export default function MenuManager() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef();
  const vidRef = useRef();

  const load = () => api.get("/restaurant/menu").then((r) => {
    setCats(r.data.categories);
    setItems(r.data.items);
  });
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    if (!newCat) return;
    await api.post("/restaurant/categories", { name: newCat, sort_order: cats.length });
    setNewCat("");
    load();
  };
  const delCat = async (id) => {
    if (!confirm("Delete category?")) return;
    await api.delete(`/restaurant/categories/${id}`);
    load();
  };
  const toggleCat = async (c) => {
    await api.patch(`/restaurant/categories/${c.id}`, { is_visible: !c.is_visible });
    load();
  };

  const openNew = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (it) => { setForm({ ...it, tags: (it.tags || []).join(", ") }); setEditing(it); setShowForm(true); };

  const upload = async (file, kind) => {
    if (!file) return;
    if (kind === "video" && file.size > 30 * 1024 * 1024) {
      toast.error("Video must be under 30 MB. Trim to 5 seconds.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // IMPORTANT: don't set Content-Type manually — the browser needs to add its
      // own multipart boundary, which gets lost if we hardcode the header here.
      const res = await api.post(`/uploads?kind=${kind}`, fd);
      // uploads-api returns a full Supabase Storage public URL already.
      const url = res.data.url || res.data.path;
      setForm((f) => ({ ...f, [kind === "menu-video" ? "video_url" : "image_url"]: url }));
      toast.success("Uploaded");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x.msg).join(" · ")
        : (typeof d === "string" ? d : "Upload failed");
      toast.error(msg);
    }
    setUploading(false);
  };

  const save = async () => {
    if (!form.category_id) {
      toast.error("Please select a category");
      return;
    }
    if (!form.name || !form.name.trim()) {
      toast.error("Item name is required");
      return;
    }
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      tax_rate: parseFloat(form.tax_rate) || 5,
      tags: (typeof form.tags === "string" ? form.tags : (form.tags || []).join(","))
        .split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.patch(`/restaurant/items/${editing.id}`, payload);
      else await api.post("/restaurant/items", payload);
      toast.success("Saved");
      setShowForm(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(" · ")
        : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    }
  };

  const toggleAvail = async (it) => {
    await api.patch(`/restaurant/items/${it.id}`, { is_available: !it.is_available });
    load();
  };
  const delItem = async (id) => {
    if (!confirm("Delete item?")) return;
    await api.delete(`/restaurant/items/${id}`);
    load();
  };

  const itemsByCat = cats.map((c) => ({ ...c, items: items.filter((i) => i.category_id === c.id) }));

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-6xl">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading text-2xl font-bold text-brand-dark">Menu Manager</h1>
          <Button onClick={openNew} className="bg-brand-orange text-white" data-testid="new-item">
            <Plus size={14} className="mr-1" /> New item
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-brand-dark/5 p-4 mb-4">
          <div className="text-xs uppercase tracking-widest text-brand-dark/50 mb-2">Categories</div>
          <div className="flex gap-2 flex-wrap">
            {cats.map((c) => (
              <div key={c.id} className={`px-3 py-1.5 rounded-full text-sm border flex items-center gap-2 ${c.is_visible ? "bg-brand-dark text-brand-light border-brand-dark" : "bg-white text-brand-dark/50 border-brand-dark/10"}`} data-testid={`cat-chip-${c.id}`}>
                {c.name}
                <button onClick={() => toggleCat(c)} className="text-xs">{c.is_visible ? "✓" : "○"}</button>
                <button onClick={() => delCat(c.id)} className="text-xs opacity-70 hover:opacity-100"><Trash2 size={12} /></button>
              </div>
            ))}
            <div className="flex gap-1">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Add category" className="h-8 text-sm w-40" data-testid="new-cat-input" />
              <Button onClick={addCat} size="sm" variant="outline" data-testid="new-cat-btn">Add</Button>
            </div>
          </div>
        </div>

        {itemsByCat.map((c) => (
          <div key={c.id} className="mb-5">
            <div className="font-heading font-semibold text-brand-dark mb-2">{c.name}</div>
            <div className="grid gap-2">
              {c.items.length === 0 && <div className="text-xs text-brand-dark/50 py-2">No items in this category yet.</div>}
              {c.items.map((it) => (
                <div key={it.id} className="bg-white rounded-lg border border-brand-dark/5 p-3 flex gap-3 items-center" data-testid={`mgr-item-${it.id}`}>
                  <img src={it.image_url} alt="" className="w-14 h-14 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-dark truncate">{it.name}</div>
                    <div className="text-xs text-brand-dark/60 truncate">{it.description}</div>
                    <div className="text-xs text-brand-dark/60 mt-0.5">
                      {inr(it.price)} · {it.is_veg ? "Veg" : "Non-veg"} · GST {it.tax_rate}%
                      {it.video_url && <span className="ml-2 text-brand-orange">● Video</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleAvail(it)} className={`text-xs px-2 py-1 rounded ${it.is_available ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`} data-testid={`toggle-avail-${it.id}`}>
                      {it.is_available ? "Available" : "Off"}
                    </button>
                    <button onClick={() => openEdit(it)} className="text-xs text-brand-dark/70 hover:text-brand-orange" data-testid={`edit-${it.id}`}>Edit</button>
                    <button onClick={() => delItem(it.id)} className="text-red-500 hover:text-red-700" data-testid={`del-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs text-brand-dark/60">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
            </div>
            <div>
              <label className="text-xs text-brand-dark/60">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-brand-dark/60">Price (₹)</label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
              </div>
              <div>
                <label className="text-xs text-brand-dark/60">GST %</label>
                <select value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: +e.target.value })} className="w-full h-10 rounded border border-input px-2">
                  <option value={5}>5%</option><option value={12}>12%</option><option value={18}>18%</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-brand-dark/60">Category</label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full h-10 rounded border border-input px-2" data-testid="form-cat">
                <option value="">Select…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex gap-3 items-center">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_veg} onChange={(e) => setForm({ ...form, is_veg: e.target.checked })} /> Veg
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_available} onChange={(e) => setForm({ ...form, is_available: e.target.checked })} /> Available
              </label>
            </div>
            <div>
              <label className="text-xs text-brand-dark/60">Tags (comma-separated)</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="bestseller, spicy, new" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-brand-dark/60 mb-1">Image</div>
                {form.image_url && <img src={form.image_url} alt="" className="w-full h-20 object-cover rounded mb-1" />}
                <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0], "menu-image")} />
                <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={uploading} data-testid="upload-image">
                  <ImagePlus size={14} className="mr-1" /> Upload
                </Button>
              </div>
              <div>
                <div className="text-xs text-brand-dark/60 mb-1">Video (max 5s / 30MB)</div>
                {form.video_url && <video src={form.video_url} className="w-full h-20 object-cover rounded mb-1" muted loop autoPlay />}
                <input ref={vidRef} type="file" accept="video/*" hidden onChange={(e) => upload(e.target.files[0], "menu-video")} />
                <Button variant="outline" size="sm" onClick={() => vidRef.current?.click()} disabled={uploading} data-testid="upload-video">
                  <Upload size={14} className="mr-1" /> Upload
                </Button>
              </div>
            </div>
            <Button onClick={save} className="w-full bg-brand-orange text-white" data-testid="save-item">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </RestaurantLayout>
  );
}
